// Browser replacement for src/main/param_metadata.js.
// Loads ArduPilot parameter metadata XML files via fetch and parses them
// with the platform-native DOMParser, avoiding the Node-only xml2js + fs stack.

const PARAM_XML_FILES = [
    { vehicle: 'ArduPlane',      url: 'Parameters/ArduPlane/apm.pdef.xml' },
    { vehicle: 'ArduCopter',     url: 'Parameters/ArduCopter/apm.pdef.xml' },
    { vehicle: 'ArduRover',      url: 'Parameters/ArduRover/apm.pdef.xml' },
    { vehicle: 'AntennaTracker', url: 'Parameters/AntennaTracker/apm.pdef.xml' },
    { vehicle: 'ArduSub',        url: 'Parameters/ArduSub/apm.pdef.xml' },
];

function processParamElement(paramEl) {
    const value = {
        humanName: paramEl.getAttribute('humanName') || '',
        documentation: paramEl.getAttribute('documentation') || '',
        user: paramEl.getAttribute('user') || '',
        fields: {},
        bitmask: {},
        values: {},
    };

    Array.from(paramEl.children).forEach(child => {
        const tag = child.tagName;
        if (tag === 'field') {
            const name = child.getAttribute('name');
            if (name) value.fields[name] = (child.textContent || '').trim();
        } else if (tag === 'bitmask') {
            Array.from(child.getElementsByTagName('bit')).forEach(bit => {
                const code = bit.getAttribute('code');
                value.bitmask[(bit.textContent || '').trim()] = code;
            });
        } else if (tag === 'values') {
            Array.from(child.getElementsByTagName('value')).forEach(v => {
                const code = v.getAttribute('code');
                value.values[(v.textContent || '').trim()] = code;
            });
        }
    });

    return value;
}

async function loadOneXml(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
    const text = await res.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'application/xml');
    const parseErr = doc.getElementsByTagName('parsererror');
    if (parseErr && parseErr.length > 0) {
        throw new Error(`XML parse error in ${url}`);
    }
    return doc;
}

function collectParamsFromContainer(containerEl, metadata, vehiclePrefix) {
    // containerEl is <vehicles> or <libraries>
    // <vehicles><parameters>...<param ... /></parameters></vehicles>
    const paramsGroups = containerEl.getElementsByTagName('parameters');
    for (const group of Array.from(paramsGroups)) {
        for (const param of Array.from(group.getElementsByTagName('param'))) {
            // Skip nested params (only direct children of <parameters>)
            if (param.parentNode !== group) continue;
            const name = param.getAttribute('name');
            if (!name) continue;
            const processed = processParamElement(param);
            metadata[name] = processed;
            // Also store under <vehicle>:<bare-name> so callers can disambiguate
            // library params that differ between vehicles (e.g. RC*_OPTION).
            if (vehiclePrefix && !name.includes(':')) {
                metadata[`${vehiclePrefix}:${name}`] = processed;
            }
        }
    }
}

export async function loadParametersXML() {
    const metadata = {};
    for (const { vehicle, url } of PARAM_XML_FILES) {
        try {
            const doc = await loadOneXml(url);
            const paramfiles = doc.getElementsByTagName('paramfile');
            if (!paramfiles.length) continue;
            const paramfile = paramfiles[0];

            const vehiclesEl = paramfile.getElementsByTagName('vehicles')[0];
            if (vehiclesEl) collectParamsFromContainer(vehiclesEl, metadata, vehicle);

            const librariesEl = paramfile.getElementsByTagName('libraries')[0];
            if (librariesEl) collectParamsFromContainer(librariesEl, metadata, vehicle);
        } catch (err) {
            console.warn('Error loading parameter XML', url, err);
        }
    }
    return metadata;
}

export default { loadParametersXML };
